/**
 * Dashboard Page
 * Requirements: 3.5
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';
import { api } from '../services/api';
import { Header } from '../components/Header';
import { Sidebar } from '../components/Sidebar';
import { JobCard } from '../components/JobCard';
import { AddJobModal } from '../components/AddJobModal';
import { AddJobFromURL } from '../components/AddJobFromURL';
import { AddJobFromEmail } from '../components/AddJobFromEmail';
import { PreferencesModal } from '../components/PreferencesModal';
import { WritingSampleModal } from '../components/WritingSampleModal';
import { SubcomponentEditor } from '../components/SubcomponentEditor';
import { ResumeEditor } from '../components/ResumeEditor';
import { JobDescriptionEditor } from '../components/JobDescriptionEditor';
import type { JobEditorTabId } from '../components/JobDescriptionEditor';
import { ImpliedFactsReviewModal } from '../components/ImpliedFactsReviewModal';
import { shouldOpenReview } from '../utils/impliedFacts';
import type { UserJob, JobPhase, SubcomponentType, UserPreferences, ImpliedFact, EditorFocusTarget } from '../types';
import { VALID_PHASES, ACTIVE_PHASES } from '../types';
import { KanbanBoard } from '../components/KanbanBoard';
import { UpgradePromptModal } from '../components/UpgradePromptModal';
import { UpgradeModal, UpgradeModalReason } from '../components/UpgradeModal';
import { StripeCheckoutModal } from '../components/StripeCheckoutModal';
import { Loader2, Inbox, LayoutGrid, Columns, CheckCircle, Info, X, Menu } from 'lucide-react';
import useJobPolling from '../hooks/useJobPolling';
import { HelpToggleButton } from '../help/HelpContext';
import useCheckoutNotification from '../hooks/useCheckoutNotification';
import useAutoFinalGeneration from '../hooks/useAutoFinalGeneration';
import { useHotkeyHandlers, OPEN_FEATURE_STATE_KEY } from '../hooks/useGlobalHotkeys';
import { jobMatchesQuery } from '../utils/jobSearch';

// Resolve URL phase param (case-insensitive) to a valid phase/filter value
function resolvePhaseParam(param: string | undefined): JobPhase | 'active' | 'all' | null {
  if (!param) return null;
  const lower = param.toLowerCase();
  if (lower === 'active') return 'active';
  if (lower === 'all') return 'all';
  const match = VALID_PHASES.find(p => p.toLowerCase() === lower);
  return match || null;
}

export function Dashboard() {
  const { phase: phaseParam } = useParams<{ phase?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const urlPhase = resolvePhaseParam(phaseParam);

  const [jobs, setJobs] = useState<UserJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedResume, setSelectedResume] = useState<string | null>(null);
  // null = "deciding": no phase chosen yet. loadJobs fetches the cheap phase
  // counts first, picks Queued/Ready from them, and only THEN fetches job rows —
  // for that one phase via the fast GSI path. Previously the initial load paid
  // for the full filter=active list (~11s on a 750-job account) only to
  // immediately switch the view to Queued/Ready anyway.
  const [selectedPhase, setSelectedPhase] = useState<JobPhase | 'active' | 'all' | null>(urlPhase || null);
  const [phaseCounts, setPhaseCounts] = useState<Record<JobPhase, number>>({} as Record<JobPhase, number>);
  const [searchQuery, setSearchQuery] = useState('');
  const [userPreferences, setUserPreferences] = useState<UserPreferences | undefined>(undefined);
  // True once the preferences fetch has SETTLED (loaded or failed). The Header
  // waits on this before it picks a resume, so a slow prefs call can't let the
  // "first resume" default win a race against the stored selection — and a
  // failed call still unblocks the default (never leaves nothing selected).
  const [prefsResolved, setPrefsResolved] = useState(false);
  // Last selection written to the server, so re-picking the same resume (or the
  // restore itself) doesn't fire a redundant PUT /preferences.
  const persistedResumeRef = useRef<string | null>(null);

  // Modals
  const [showAddJobModal, setShowAddJobModal] = useState(false);
  const [showAddJobUrlModal, setShowAddJobUrlModal] = useState(false);
  const [showAddJobEmailModal, setShowAddJobEmailModal] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [showWritingSampleModal, setShowWritingSampleModal] = useState(false);
  const [showResumeEditor, setShowResumeEditor] = useState(false);
  const [editingResumeName, setEditingResumeName] = useState<string | undefined>(undefined);
  // Recruiter Check deep-link: which finding the editor should scroll to.
  const [editorFocusTarget, setEditorFocusTarget] = useState<EditorFocusTarget | null>(null);
  const editorFocusNonceRef = useRef(0);
  const [editingSubcomponent, setEditingSubcomponent] = useState<{
    jobid: string;
    component: SubcomponentType;
    content: string;
  } | null>(null);
  const [editingJob, setEditingJob] = useState<{
    jobid: string;
    jobdesc: string;
    jobtags: string[];
    super_tags?: string[];
    jobtitle: string;
    jobcompany: string;
    joblocation: string;
    jobsalary: string;
    jobreqid: string;
    jobnotes: string;
    /** Tab the editor opens on — the job card's "Notes" link deep-links here. */
    initialTab?: JobEditorTabId;
    tagsGenerating?: boolean;
  } | null>(null);
  // Implied Facts: the run the just-closed editor dispatched, the "checking…"
  // pill while it runs, and the review lightbox once proposals land.
  const [impliedFactsRun, setImpliedFactsRun] = useState<{ resumename: string; runId: string } | null>(null);
  const [impliedFactsReview, setImpliedFactsReview] = useState<{ resumename: string; facts: ImpliedFact[] } | null>(null);

  // View mode: grid (default) or kanban
  const [viewMode, setViewMode] = useState<'grid' | 'kanban'>('grid');

  // Mobile sidebar state
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Upgrade prompt modal
  const [upgradeModal, setUpgradeModal] = useState<{open: boolean, type: 'resume_soft'|'resume_hard'|'job'|'ai'}>({open: false, type: 'resume_soft'});

  // Stripe checkout modal (triggered by Header "Upgrade Now" or ?upgrade=true URL param)
  const [showStripeCheckout, setShowStripeCheckout] = useState(false);

  // Free-gen upgrade modal (triggered by JobCard hover overlay, exhausted points, etc.)
  const [freeGenUpgradeModal, setFreeGenUpgradeModal] = useState<{ open: boolean; reason: UpgradeModalReason }>({ open: false, reason: 'general' });

  const handleUpgradeRequest = useCallback((reason: UpgradeModalReason) => {
    setFreeGenUpgradeModal({ open: true, reason });
  }, []);

  const handleFreeGenConsumed = useCallback(() => {
    setUserPreferences((prev) => {
      if (!prev || prev.free_gen_remaining === undefined) return prev;
      return { ...prev, free_gen_remaining: Math.max(0, prev.free_gen_remaining - 1) };
    });
  }, []);

  // Checkout notification hook
  const { checkoutNotification, setCheckoutNotification } = useCheckoutNotification({ setUserPreferences });

  // Auto-open Stripe checkout from ?upgrade=true URL param (e.g., from pricing page)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgrade') === 'true') {
      setShowStripeCheckout(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Resume refresh key: bump to trigger Header resume list reload without full page refresh
  const [resumeRefreshKey, setResumeRefreshKey] = useState(0);

  // Track whether initial phase selection has been applied
  const initialPhaseApplied = useRef(false);

  // Pending optimistic phase changes — jobid → {target phase, expiry timestamp}.
  // Needed because useJobPolling fires loadJobs(true) every 2s when any job is
  // Generating, and the server write for a phase change can take ~10s. Without
  // this overlay, each poll overwrites the optimistic phase with the still-old
  // server phase, causing the card to snap back repeatedly. Entries are cleared
  // when the server catches up (server phase == pending phase) or after 30s
  // (safety TTL so a truly-failed write doesn't leave the card stuck).
  const pendingPhaseChanges = useRef<Map<string, { phase: JobPhase; expires: number }>>(new Map());
  const PENDING_PHASE_TTL_MS = 30_000;

  // Monotonic load sequence — invalidates in-flight fetches and page drains
  // the moment a newer load starts (phase switch, view switch, drift refetch).
  const loadSeqRef = useRef(0);
  const PAGE_SIZE = 200;

  // Reconcile a server page with any in-flight optimistic phase changes.
  // Pass `counts` to also shift phaseCounts for still-pending moves (first
  // page / legacy full response); pass null for append-only pages.
  const applyPendingOverlay = useCallback((serverJobs: UserJob[], counts: Record<JobPhase, number> | null) => {
    const now = Date.now();
    return serverJobs.map((j) => {
      const pending = pendingPhaseChanges.current.get(j.jobid);
      if (!pending) return j;
      // Server caught up, OR the overlay expired — drop the pending entry.
      if (j.jobphase === pending.phase || now > pending.expires) {
        pendingPhaseChanges.current.delete(j.jobid);
        return j;
      }
      // Still pending — keep the optimistic phase + adjust phaseCounts to match.
      const serverPhase = j.jobphase;
      if (counts && serverPhase !== pending.phase) {
        counts[serverPhase] = Math.max(0, (counts[serverPhase] || 0) - 1);
        counts[pending.phase] = (counts[pending.phase] || 0) + 1;
      }
      return { ...j, jobphase: pending.phase };
    });
  }, []);

  const loadJobs = useCallback(async (isBackgroundRefresh = false) => {
    const seq = ++loadSeqRef.current;
    // Only show loading spinner on initial load or explicit refresh, not during background polling
    if (!isBackgroundRefresh) {
      setLoading(true);
    }
    setError(null);
    // Set in the phase-deciding path so the finally below leaves the spinner up
    // until the follow-up (phase-specific) fetch lands — avoids an empty-state flash.
    let deciding = false;
    try {
      // Aggregate views (All Jobs / All Active / Kanban) are PAGED: render the
      // first page immediately (fast first paint), then drain the remaining
      // pages in the background and append. Search and Kanban columns operate
      // on the loaded set, which converges to the full set within seconds.
      if (viewMode === 'kanban' || selectedPhase === 'active' || selectedPhase === 'all') {
        const f: 'active' | 'all' = (viewMode === 'kanban' || selectedPhase === 'all') ? 'all' : 'active';
        const first = await api.listJobs(undefined, f, { limit: PAGE_SIZE });
        if (seq !== loadSeqRef.current) return;
        const counts = { ...(first.phaseCounts || {}) } as Record<JobPhase, number>;
        const merged = applyPendingOverlay(first.jobs || [], counts);
        setJobs(merged);
        setPhaseCounts(counts);
        // Background drain — deliberately NOT awaited so the spinner drops
        // after page one. Best-effort: a failed page just stops the drain.
        void (async () => {
          let cursor = first.cursor || undefined;
          while (cursor && seq === loadSeqRef.current) {
            try {
              const page = await api.listJobs(undefined, f, { limit: PAGE_SIZE, cursor });
              if (seq !== loadSeqRef.current) return;
              const pageMerged = applyPendingOverlay(page.jobs || [], null);
              setJobs((prev) => {
                const seen = new Set(prev.map((p) => p.jobid));
                return [...prev, ...pageMerged.filter((p) => !seen.has(p.jobid))];
              });
              cursor = page.cursor || undefined;
            } catch {
              return;
            }
          }
        })();
        return;
      }

      let response;
      if (selectedPhase === null) {
        // Initial load, no phase in the URL: fetch only the cheap counts, pick
        // the default phase (Queued if any, else Ready), and let the state
        // change re-run loadJobs for just that phase.
        deciding = true;
        const counts = await api.getJobCounts();
        if (seq !== loadSeqRef.current) return;
        setPhaseCounts({ ...(counts.phaseCounts || {}) } as Record<JobPhase, number>);
        initialPhaseApplied.current = true;
        setSelectedPhase(((counts.phaseCounts?.['Queued'] || 0) > 0) ? 'Queued' : 'Ready');
        return;
      }
      response = await api.listJobs(selectedPhase);
      if (seq !== loadSeqRef.current) return;

      // Reconcile server response with any in-flight optimistic phase changes
      const serverJobs: UserJob[] = response.jobs || [];
      const serverPhaseCounts: Record<JobPhase, number> = { ...(response.phaseCounts || {}) };
      const mergedJobs = applyPendingOverlay(serverJobs, serverPhaseCounts);
      // The list endpoint now does server-side phase filtering, so a job that
      // optimistically moved to a *different* phase than the current view
      // simply won't appear in serverJobs. If we don't sweep here, the
      // pending entry sticks around until its 30s TTL and the next time the
      // user visits the OLD tab the job won't be filtered out by the overlay
      // (but it also won't appear in the response since it's moved), so it's
      // harmless — yet stale entries accumulate. Clear them once the job is
      // no longer visible in the current view.
      const serverIds = new Set(serverJobs.map((j) => j.jobid));
      for (const [jobid, p] of pendingPhaseChanges.current.entries()) {
        if (!serverIds.has(jobid) && p.phase !== selectedPhase) {
          pendingPhaseChanges.current.delete(jobid);
        }
      }
      setJobs(mergedJobs);
      setPhaseCounts(serverPhaseCounts);
    } catch (err: any) {
      if (seq !== loadSeqRef.current) return; // superseded — don't clobber state
      console.error('Failed to load jobs:', err);
      setError(err?.message || 'Failed to load jobs');
      setJobs([]);
    } finally {
      if (!isBackgroundRefresh && !deciding && seq === loadSeqRef.current) {
        setLoading(false);
      }
    }
  }, [selectedPhase, viewMode, applyPendingOverlay]);

  // Sync URL with selected phase (null = still deciding the default — no URL yet)
  useEffect(() => {
    if (selectedPhase === null) return;
    const urlPath = selectedPhase === 'active' ? '/' : `/${selectedPhase}`;
    const currentPath = window.location.pathname;
    if (currentPath !== urlPath) {
      navigate(urlPath, { replace: true });
    }
  }, [selectedPhase, navigate]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  // (Dynamic default phase now happens inside loadJobs' counts-first path:
  // counts_only -> pick Queued/Ready -> fetch just that phase. The old
  // post-load effect that re-picked the phase after a full active fetch is
  // gone — that full fetch no longer happens on initial load.)
  useEffect(() => {
    if (urlPhase) initialPhaseApplied.current = true;
  }, [urlPhase]);

  // Load user preferences on mount
  useEffect(() => {
    async function loadPreferences() {
      try {
        const prefs = await api.getPreferences();
        setUserPreferences(prefs);
        persistedResumeRef.current = prefs.selected_resume || null;
      } catch (err: any) {
        console.error('Failed to load user preferences:', err);
        const status = err?.response?.status;
        // 403 on preferences means user exists in Cognito but not in backend DB.
        // Clear auth state and redirect to login.
        if (status === 403) {
          console.warn('User not found in backend (403). Clearing auth state.');
          try {
            await signOut();
          } catch {
            localStorage.clear();
            sessionStorage.clear();
          }
          window.location.href = '/';
        }
      } finally {
        setPrefsResolved(true);
      }
    }
    loadPreferences();
  }, []);

  // Header selector + Edit Resume "switch resume" both route here so the pick
  // is persisted server-side. Fire-and-forget — a failed write never blocks or
  // reverts the UI, it just means the selection isn't sticky next load.
  function handleSelectResume(name: string) {
    setSelectedResume(name);
    if (persistedResumeRef.current === name) return;
    persistedResumeRef.current = name;
    setUserPreferences(prev => (prev ? { ...prev, selected_resume: name } : prev));
    api.updatePreferences({ selected_resume: name }).catch(err => {
      console.error('Failed to persist selected resume:', err);
      // Let a later pick of the same resume retry the write.
      if (persistedResumeRef.current === name) persistedResumeRef.current = null;
    });
  }

  // Merge a single freshly-polled job into local state. Honors the
  // pendingPhaseChanges overlay (so user-initiated Move To wins over a
  // concurrent server-side phase transition) and delta-updates phaseCounts
  // on phase change so the sidebar stays in sync without a full refetch.
  const handlePollingUpdate = useCallback((updatedJob: UserJob) => {
    const old = jobs.find((j) => j.jobid === updatedJob.jobid);
    if (!old) return;

    const pending = pendingPhaseChanges.current.get(updatedJob.jobid);
    const effectivePhase = pending && Date.now() < pending.expires
      ? pending.phase
      : updatedJob.jobphase;

    setJobs((prev) => prev.map((j) =>
      j.jobid === updatedJob.jobid
        ? { ...j, ...updatedJob, jobphase: effectivePhase }
        : j
    ));

    if (old.jobphase !== effectivePhase) {
      const oldPhase = old.jobphase;
      setPhaseCounts((prev) => ({
        ...prev,
        [oldPhase]: Math.max(0, (prev[oldPhase] || 0) - 1),
        [effectivePhase]: (prev[effectivePhase] || 0) + 1,
      }));
    }
  }, [jobs]);

  // Job polling (generation tracking, per-job active polling, 30s full refresh)
  // Cheap 30s background check: one counts-only call; full list refetch ONLY
  // when the counts drift (new jobs, server-side phase moves). Keeps an idle
  // open tab from refetching hundreds of jobs every 30 seconds.
  const phaseCountsRef = useRef(phaseCounts);
  useEffect(() => { phaseCountsRef.current = phaseCounts; }, [phaseCounts]);
  const backgroundPoll = useCallback(async () => {
    const counts = await api.getJobCounts();
    const next = (counts.phaseCounts || {}) as Record<JobPhase, number>;
    const cur = phaseCountsRef.current || ({} as Record<JobPhase, number>);
    const drifted = VALID_PHASES.some((p) => (next[p] || 0) !== (cur[p] || 0));
    if (drifted) await loadJobs(true);
  }, [loadJobs]);

  useJobPolling({ jobs, loadJobs, onPollingUpdate: handlePollingUpdate, onBackgroundPoll: backgroundPoll });

  // Debounced copy of the search query for FILTERING — the input itself stays
  // on searchQuery so typing is instant, but re-filtering (and re-rendering
  // hundreds of cards) waits for a 250ms pause in typing.
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery), 250);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Filter jobs based on search query
  const filteredJobs = useMemo(() => {
    // Client-side phase filter so optimistic phase changes visually remove the
    // card from the current tab the instant the user clicks Move To, rather
    // than waiting for the next background poll (up to 30s) to re-fetch a
    // phase-filtered list from the server. Kanban view shows every phase in
    // its own column, so it never filters here.
    let result = jobs;
    if (viewMode !== 'kanban') {
      if (selectedPhase === 'active') {
        result = result.filter(job => ACTIVE_PHASES.includes(job.jobphase));
      } else if (selectedPhase !== 'all') {
        result = result.filter(job => job.jobphase === selectedPhase);
      }
    }
    // `jobMatchesQuery` guards every field: this runs inside a useMemo during
    // render, so a TypeError on a card with a missing jobtitle/jobcompany takes
    // down the whole app via the error boundary rather than failing gracefully.
    if (debouncedQuery.trim()) {
      result = result.filter((job) => jobMatchesQuery(job, debouncedQuery));
    }
    return result;
  }, [jobs, debouncedQuery, selectedPhase, viewMode]);

  // Auto-final-generation hook
  const { handleGenerateResume } = useAutoFinalGeneration({ jobs, loadJobs });

  // Optimistic job update: replace a single job in the jobs array without re-fetching
  const handleJobUpdate = useCallback((updatedJob: UserJob) => {
    setJobs(prev => prev.map(j =>
      j.jobid === updatedJob.jobid ? updatedJob : j
    ));
  }, []);

  // Optimistic phase change for kanban drag-and-drop + JobCard Skip / Move To / Generate.
  // Also records a pending entry so background polling (useJobPolling, 2s interval
  // during generation) doesn't overwrite this with stale server state before the
  // write lands. See pendingPhaseChanges comment for details.
  const handleOptimisticPhaseChange = useCallback((jobid: string, newPhase: JobPhase) => {
    pendingPhaseChanges.current.set(jobid, {
      phase: newPhase,
      expires: Date.now() + PENDING_PHASE_TTL_MS,
    });
    setJobs(prev => prev.map(j =>
      j.jobid === jobid
        // Moving INTO Interviewing kicks off server-side question matching; show
        // the card immediately with a 'matching' marker so its primary button
        // reads "matching interview questions…" until the phase-change response
        // (which carries the matches) flips it to 'done'.
        ? {
            ...j,
            jobphase: newPhase,
            ...(newPhase === 'Interviewing'
              ? { interview_match_state: 'matching' as const, interview_matches: undefined }
              : {}),
          }
        : j
    ));
    setPhaseCounts(prev => {
      const oldPhase = jobs.find(j => j.jobid === jobid)?.jobphase;
      if (!oldPhase || oldPhase === newPhase) return prev;
      return {
        ...prev,
        [oldPhase]: Math.max(0, (prev[oldPhase] || 0) - 1),
        [newPhase]: (prev[newPhase] || 0) + 1,
      };
    });
  }, [jobs]);

  async function handleEditSubcomponent(jobid: string, component: SubcomponentType) {
    // The /jobs list response slims data* fields to truthy markers ('1') to
    // stay under Lambda's 6MB response limit, so cached content here is the
    // marker — fetch the full job to get the real subcomponent text.
    try {
      const fullJob = await api.getJob(jobid);
      const content = fullJob[`data${component}` as keyof UserJob] as string || '';
      setEditingSubcomponent({ jobid, component, content });
    } catch (err) {
      console.error('Failed to load subcomponent content:', err);
      setEditingSubcomponent({ jobid, component, content: '' });
    }
  }

  async function handleEditJob(jobid: string, initialTab?: JobEditorTabId) {
    const listJob = jobs.find((j) => j.jobid === jobid);
    if (listJob) {
      // The /jobs list response drops jobdesc (and jobnotes) to stay under
      // Lambda's 6MB limit, so fetch the full job for the real description +
      // note (mirrors the data* subcomponent pattern in handleEditSubcomponent
      // above). Falls back to the list object if the detail fetch fails.
      let job = listJob;
      try {
        job = await api.getJob(jobid);
      } catch (err) {
        console.error('Failed to load full job for edit:', err);
      }
      const currentTags = job.jobtags || [];
      const baseEditing = {
        jobid,
        jobdesc: job.jobdesc,
        jobtags: currentTags,
        super_tags: job.super_tags || [],
        jobtitle: job.jobtitle || '',
        jobcompany: job.jobcompany || '',
        joblocation: job.joblocation || '',
        jobsalary: job.jobsalary || '',
        jobreqid: job.jobreqid || '',
        jobnotes: job.jobnotes || '',
        initialTab,
      };
      setEditingJob(baseEditing);

      // Auto-regenerate tags if the job has fewer than 5
      if (currentTags.length < 5 && job.jobdesc) {
        setEditingJob({ ...baseEditing, tagsGenerating: true });

        // Trigger tag generation by re-saving the job description (backend fires async gen_tags Lambda)
        api.updateJobDescription(jobid, job.jobdesc).catch((err) => {
          console.warn('Background tag generation trigger failed:', err);
        });

        // Poll for updated tags (gen_tags Lambda takes ~3-5s)
        let attempts = 0;
        const maxAttempts = 5;
        const pollInterval = 3000;
        const tagPollTimer = setInterval(async () => {
          attempts++;
          try {
            const refreshedJob = await api.getJob(jobid);
            const newTags = refreshedJob.jobtags || [];
            if (newTags.length > currentTags.length || attempts >= maxAttempts) {
              clearInterval(tagPollTimer);
              if (newTags.length > currentTags.length) {
                // Update editingJob state so JobDescriptionEditor picks up new tags
                setEditingJob((prev) =>
                  prev && prev.jobid === jobid
                    ? { ...prev, jobtags: newTags, tagsGenerating: false }
                    : prev
                );
                // Also update the job in the main jobs list
                handleJobUpdate(refreshedJob);
              } else {
                // Max attempts reached without new tags — stop the indicator
                setEditingJob((prev) =>
                  prev && prev.jobid === jobid
                    ? { ...prev, tagsGenerating: false }
                    : prev
                );
              }
            }
          } catch (err) {
            console.warn('Tag poll failed:', err);
            clearInterval(tagPollTimer);
            setEditingJob((prev) =>
              prev && prev.jobid === jobid
                ? { ...prev, tagsGenerating: false }
                : prev
            );
          }
        }, pollInterval);
      }
    }
  }

  function handleRegenerateTags() {
    if (!editingJob) return;
    const { jobid, jobdesc } = editingJob;

    // Snapshot the existing tags so we can (a) detect when regeneration has
    // actually produced a NEW set and (b) restore them if it fails/times out.
    // We do NOT persist an empty list to the backend — gen_tags REPLACES
    // jobtags on success, so the old tags survive until new ones land. This
    // prevents the "regenerate → zero tags" data loss when generation is
    // skipped or errors. The clear below is UI-only (visual "regenerating").
    const oldTags = editingJob.jobtags || [];
    const oldTagsJson = JSON.stringify(oldTags);
    setEditingJob(prev => prev ? { ...prev, jobtags: [], tagsGenerating: true } : prev);

    // Trigger regeneration via a job-description re-save (force=True on the
    // backend bypasses the enabled-cost-guard for this explicit user action).
    api.updateJobDescription(jobid, jobdesc).catch(err => {
      console.warn('Regenerate tags trigger failed:', err);
    });

    // Poll until the tag set CHANGES from the snapshot (new tags arrived) or we
    // time out. On timeout we restore the snapshot rather than show zero.
    let attempts = 0;
    const maxAttempts = 20; // 20 x 3s = 60s
    const pollInterval = 3000;
    const tagPollTimer = setInterval(async () => {
      attempts++;
      try {
        const refreshedJob = await api.getJob(jobid);
        const newTags = refreshedJob.jobtags || [];
        const changed = JSON.stringify(newTags) !== oldTagsJson && newTags.length > 0;
        if (changed || attempts >= maxAttempts) {
          clearInterval(tagPollTimer);
          // Prefer the freshly generated set; if none arrived, keep the old tags.
          const finalTags = changed ? newTags : oldTags;
          setEditingJob(prev =>
            prev && prev.jobid === jobid
              ? {
                  ...prev,
                  jobtags: finalTags,
                  // Refresh tag categories too — gen_tags regenerates super_tags
                  // alongside jobtags, so the Tag Categories section must update.
                  ...(changed ? { super_tags: refreshedJob.super_tags || [] } : {}),
                  tagsGenerating: false,
                }
              : prev
          );
          if (changed) {
            handleJobUpdate(refreshedJob);
          }
        }
      } catch (err) {
        console.warn('Tag regen poll failed:', err);
        clearInterval(tagPollTimer);
        // Restore the snapshot on poll failure — never leave the user at zero.
        setEditingJob(prev =>
          prev && prev.jobid === jobid
            ? { ...prev, jobtags: oldTags, tagsGenerating: false }
            : prev
        );
      }
    }, pollInterval);
  }

  async function handleSaveSubcomponent(content: string) {
    if (!editingSubcomponent) return;
    // Cover letter edits regenerate the PDF as part of the save and open it in
    // a new tab (owner request 2026-08-08 — the old flow was save, then
    // manually regenerate the file, then download). The tab MUST be opened
    // synchronously, before the first await, or popup blockers eat it; it
    // shows a placeholder while the ~10s render runs, then navigates.
    const isCoverletter = editingSubcomponent.component === 'coverletter';
    const jobid = editingSubcomponent.jobid;
    let pdfTab: Window | null = null;
    if (isCoverletter) {
      pdfTab = window.open('', '_blank');
      try {
        pdfTab?.document.write(
          '<title>Cover Letter</title><body style="font-family:sans-serif;color:#444;'
          + 'display:flex;align-items:center;justify-content:center;height:90vh">'
          + '<p>Saving your edits and regenerating the cover letter PDF&hellip;</p></body>');
      } catch { /* cross-origin quirk — placeholder is cosmetic */ }
    }
    try {
      await api.updateSubcomponentContent(
        editingSubcomponent.jobid,
        editingSubcomponent.component,
        content
      );
      if (isCoverletter) {
        const resp = await api.generateFinalFiles(jobid, 'cover');
        const pdfUrl = resp?.files?.cover_letter_pdf;
        if (pdfUrl) {
          // Cache-bust: the public URL is stable and CloudFront may still hold
          // the previous render.
          const fresh = `${pdfUrl}${pdfUrl.includes('?') ? '&' : '?'}cb=${Date.now()}`;
          if (pdfTab && !pdfTab.closed) pdfTab.location.href = fresh;
          else window.open(fresh, '_blank');
        } else {
          pdfTab?.close();
        }
      }
      await loadJobs();
    } catch (error) {
      pdfTab?.close();
      console.error('Failed to save subcomponent:', error);
      throw error; // Re-throw so SubcomponentEditor can handle it
    }
  }

  // ---- Implied Facts: wait for the post-close extraction, then review -------
  // The editor fires the trigger on its way out and hands us the run_id; it is
  // unmounting, so the wait lives here. We poll the resume record (the marker
  // and the facts both ride along on getResume — there is no status endpoint)
  // until THIS run reports done, which is what stops a stale marker from an
  // earlier run popping the modal.
  useEffect(() => {
    if (!impliedFactsRun) return;
    let cancelled = false;
    const startedAt = Date.now();
    const MAX_WAIT_MS = 60000;
    const INTERVAL_MS = 4000;

    const tick = async () => {
      if (cancelled) return;
      try {
        const data = await api.getResume(impliedFactsRun.resumename);
        if (cancelled) return;
        const facts = data.resumejson?.implied_facts;
        if (shouldOpenReview({
          runId: impliedFactsRun.runId,
          state: data.implied_facts_state,
          facts,
          editorOpen: false,   // the effect is torn down when the editor reopens
        })) {
          setImpliedFactsReview({ resumename: impliedFactsRun.resumename, facts: facts || [] });
          setImpliedFactsRun(null);
          return;
        }
        if (data.implied_facts_state?.run_id === impliedFactsRun.runId
            && data.implied_facts_state?.status === 'done') {
          // Finished with nothing new to review — stop quietly.
          setImpliedFactsRun(null);
          return;
        }
      } catch {
        // transient — keep polling until the timeout
      }
      if (cancelled) return;
      if (Date.now() - startedAt > MAX_WAIT_MS) {
        setImpliedFactsRun(null);
        return;
      }
      setTimeout(tick, INTERVAL_MS);
    };
    const first = setTimeout(tick, INTERVAL_MS);

    return () => { cancelled = true; clearTimeout(first); };
    // Reopening the editor cancels the wait: the section card shows the same
    // proposals in place, so a modal on top of it would be redundant.
  }, [impliedFactsRun, showResumeEditor]);

  useEffect(() => {
    if (showResumeEditor) {
      setImpliedFactsRun(null);
      setImpliedFactsReview(null);
    }
  }, [showResumeEditor]);

  async function saveImpliedFacts(resumename: string, facts: ImpliedFact[]) {
    // Re-read first so we PUT the server's current resume with ONLY the facts
    // array changed — the async tag/synonym pipeline may have written since.
    const current = await api.getResume(resumename);
    await api.updateResume(resumename, { ...current.resumejson, implied_facts: facts });
  }

  async function handleNewResume() {
    // Check resume count against limits
    try {
      const response = await api.listResumes();
      const resumeCount = response.resumes?.length || 0;
      const status = userPreferences?.status;
      const allowedResumes = userPreferences?.allowed_master_resumes;
      const softCap = 5;

      // Hard cap for trial/expired users (default 1 for trial, 1 for expired)
      if (status === 'trial' || status === 'expired') {
        const hardLimit = allowedResumes ?? 1;
        if (resumeCount >= hardLimit) {
          setUpgradeModal({ open: true, type: 'resume_hard' });
          return;
        }
      }

      // Soft cap for active users
      if (status === 'active' || status === 'hibernating') {
        const limit = allowedResumes ?? softCap;
        if (resumeCount >= limit) {
          setUpgradeModal({ open: true, type: 'resume_soft' });
          return;
        }
      }
    } catch {
      // If we can't check, allow creation
    }

    setEditingResumeName(undefined);
    // A finding deep-link must never replay onto a brand-new resume (closing
    // the editor resets the once-per-nonce guard, so a stale target would).
    setEditorFocusTarget(null);
    setShowResumeEditor(true);
  }

  function handleEditResume(
    resumename: string,
    focus?: Omit<EditorFocusTarget, 'nonce'>,
  ) {
    setEditingResumeName(resumename);
    // The nonce is what lets the SAME finding re-trigger the editor's scroll:
    // without it a second click passes an identical object and the editor's
    // once-per-nonce guard would swallow it. No target = plain open, and the
    // stale one is cleared so it can't fire on an unrelated open.
    setEditorFocusTarget(focus ? { ...focus, nonce: ++editorFocusNonceRef.current } : null);
    setShowResumeEditor(true);
  }

  async function handleDeleteResume(resumename: string) {
    await api.deleteResume(resumename);
  }

  // --- Global feature hotkeys -------------------------------------------
  // Resume Editor (r) and Writing Sample (w) are Dashboard-owned lightboxes, so
  // the Dashboard handles them IN PLACE via the same handlers the Feature
  // Selector uses. The provider only routes here when the key was pressed on
  // another page (see hooks/useGlobalHotkeys.ts).
  useHotkeyHandlers({
    'resume-editor': () => {
      if (selectedResume) {
        handleEditResume(selectedResume);
      } else {
        handleNewResume();
      }
    },
    'writing-sample': () => setShowWritingSampleModal(true),
  });

  // Arrived from another page carrying `location.state.openFeature`.
  const [pendingFeature, setPendingFeature] = useState<string | null>(null);
  useEffect(() => {
    const state = location.state as Record<string, unknown> | null;
    const requested = state?.[OPEN_FEATURE_STATE_KEY];
    if (typeof requested !== 'string') return;
    setPendingFeature(requested);
    // Drop the state so a refresh or Back doesn't reopen the modal.
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  useEffect(() => {
    if (!pendingFeature) return;
    if (pendingFeature === 'writing-sample') {
      setShowWritingSampleModal(true);
      setPendingFeature(null);
      return;
    }
    if (pendingFeature !== 'resume-editor') {
      setPendingFeature(null);
      return;
    }
    // Resumes load asynchronously (the Header populates `selectedResume`), so
    // wait briefly rather than mistaking "not loaded yet" for "no resumes" and
    // dropping the user into the new-resume flow.
    if (selectedResume) {
      handleEditResume(selectedResume);
      setPendingFeature(null);
      return;
    }
    const timer = setTimeout(() => {
      setPendingFeature(null);
      handleNewResume();
    }, 2500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFeature, selectedResume]);

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Skip to main content link (WCAG 2.4.1) */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-2 focus:left-2 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:text-sm focus:font-medium"
      >
        Skip to main content
      </a>

      {/* Checkout result notification banner */}
      {checkoutNotification && (
        <div className={`flex items-center justify-between px-4 py-3 ${
          checkoutNotification.type === 'success'
            ? 'bg-green-50 text-green-800 border-b border-green-200'
            : 'bg-yellow-50 text-yellow-800 border-b border-yellow-200'
        }`}>
          <div className="flex items-center">
            {checkoutNotification.type === 'success' ? (
              <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            ) : (
              <Info className="w-5 h-5 mr-2 flex-shrink-0" />
            )}
            <span className="text-sm font-medium">{checkoutNotification.message}</span>
          </div>
          <button
            onClick={() => setCheckoutNotification(null)}
            className="p-1 hover:bg-black/5 rounded"
            aria-label="Dismiss notification"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <Header
        onAddJobUrl={() => setShowAddJobUrlModal(true)}
        onAddJobManual={() => setShowAddJobModal(true)}
        onAddJobEmail={() => setShowAddJobEmailModal(true)}
        onOpenSettings={() => setShowPreferencesModal(true)}
        onOpenWritingSample={() => setShowWritingSampleModal(true)}
        onUpgrade={() => setShowStripeCheckout(true)}
        selectedResume={selectedResume}
        onSelectResume={handleSelectResume}
        onEditResume={handleEditResume}
        onNewResume={handleNewResume}
        userPreferences={userPreferences}
        prefsResolved={prefsResolved}
        onPreferencesChange={(prefs) => setUserPreferences(prev => prev ? { ...prev, ...prefs } : prev)}
        resumeRefreshKey={resumeRefreshKey}
      />

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - desktop: always visible; mobile: slide-in drawer */}
        {viewMode === 'grid' && (
          <>
            {/* Desktop sidebar (hidden on mobile) */}
            <div className="hidden md:block">
              <Sidebar
                selectedPhase={selectedPhase}
                onSelectPhase={setSelectedPhase}
                phaseCounts={phaseCounts}
                onAddJobUrl={() => setShowAddJobUrlModal(true)}
                onAddJobManual={() => setShowAddJobModal(true)}
                onAddJobEmail={() => setShowAddJobEmailModal(true)}
                userPreferences={userPreferences}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
              />
            </div>

            {/* Mobile sidebar overlay */}
            {mobileSidebarOpen && (
              <div
                className="md:hidden fixed inset-0 bg-black/40 z-40"
                onClick={() => setMobileSidebarOpen(false)}
              />
            )}
            <div
              className={`md:hidden fixed top-0 left-0 h-full w-64 bg-white z-50 transform transition-transform duration-200 ease-in-out ${
                mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
              }`}
            >
              <Sidebar
                selectedPhase={selectedPhase}
                onSelectPhase={(phase) => {
                  setSelectedPhase(phase);
                  setMobileSidebarOpen(false);
                }}
                phaseCounts={phaseCounts}
                onAddJobUrl={() => { setShowAddJobUrlModal(true); setMobileSidebarOpen(false); }}
                onAddJobManual={() => { setShowAddJobModal(true); setMobileSidebarOpen(false); }}
                onAddJobEmail={() => { setShowAddJobEmailModal(true); setMobileSidebarOpen(false); }}
                userPreferences={userPreferences}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
              />
            </div>
          </>
        )}

        <main id="main-content" className={`flex-1 overflow-y-auto ${viewMode === 'kanban' ? 'p-3 overflow-x-auto' : 'p-6'}`}>
          {/* View mode toggle + mobile sidebar button */}
          <div className="flex items-center justify-between mb-3">
            {/* Mobile sidebar toggle (only in grid mode) */}
            {viewMode === 'grid' ? (
              <button
                onClick={() => setMobileSidebarOpen(true)}
                className="md:hidden p-1.5 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                title="Show filters"
                aria-label="Show filters"
              >
                <Menu className="w-5 h-5" />
              </button>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-1 ml-auto">
            <button
              onClick={() => setViewMode('grid')}
              data-help-target="mainapp.viewmain"
              className={`p-1.5 rounded transition-colors ${
                viewMode === 'grid'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-500 hover:text-gray-600 hover:bg-gray-100'
              }`}
              title="Grid view"
              aria-label="Grid view"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('kanban')}
              data-help-target="mainapp.viewkanban"
              className={`p-1.5 rounded transition-colors ${
                viewMode === 'kanban'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-500 hover:text-gray-600 hover:bg-gray-100'
              }`}
              title="Kanban board"
              aria-label="Kanban board"
            >
              <Columns className="w-4 h-4" />
            </button>
            <HelpToggleButton />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64" role="status" aria-label="Loading jobs">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-64 text-red-500">
              <p className="text-lg">Error loading jobs</p>
              <p className="text-sm">{error}</p>
              <button
                onClick={() => loadJobs()}
                className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Retry
              </button>
            </div>
          ) : viewMode === 'kanban' ? (
            <KanbanBoard
              jobs={jobs}
              phaseCounts={phaseCounts}
              onRefresh={loadJobs}
              onEditSubcomponent={handleEditSubcomponent}
              onEditJob={handleEditJob}
              onGenerateResume={handleGenerateResume}
              onOptimisticPhaseChange={handleOptimisticPhaseChange}
              onJobUpdate={handleJobUpdate}
              selectedResume={selectedResume}
              autoDownloadFiles={userPreferences?.auto_download_files !== false}
              userPreferences={userPreferences}
              onUpgradeRequest={handleUpgradeRequest}
              onFreeGenConsumed={handleFreeGenConsumed}
              onEditResume={selectedResume ? (target) => handleEditResume(selectedResume, target) : undefined}
            />
          ) : filteredJobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Inbox className="w-12 h-12 mb-4" />
              <p className="text-lg">{searchQuery ? 'No matching jobs' : 'No jobs found'}</p>
              <p className="text-sm">{searchQuery ? 'Try a different search term' : 'Add a job to get started'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
              {filteredJobs.map((job) => (
                // content-visibility:auto lets the browser skip layout/paint for
                // offscreen cards — with hundreds of jobs loaded, only the
                // viewport's worth is rendered. contain-intrinsic-size reserves
                // an estimated height so the scrollbar doesn't jump.
                <div key={job.jobid} className="[content-visibility:auto] [contain-intrinsic-size:auto_460px]">
                  <JobCard
                    job={job}
                    onRefresh={loadJobs}
                    onEditSubcomponent={handleEditSubcomponent}
                    onEditJob={handleEditJob}
                    onGenerateResume={handleGenerateResume}
                    onJobUpdate={handleJobUpdate}
                    onOptimisticPhaseChange={handleOptimisticPhaseChange}
                    selectedResume={selectedResume}
                    autoDownloadFiles={userPreferences?.auto_download_files !== false}
                    userPreferences={userPreferences}
                    onUpgradeRequest={handleUpgradeRequest}
                    onFreeGenConsumed={handleFreeGenConsumed}
                    // Recruiter Check's "fix once, fixes every resume" findings
                    // are fixed in Edit Resume — this opens it on the resume the
                    // card generates from.
                    onEditResume={selectedResume ? (target) => handleEditResume(selectedResume, target) : undefined}
                  />
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Modals */}
      <AddJobModal
        isOpen={showAddJobModal}
        onClose={() => setShowAddJobModal(false)}
        onSuccess={loadJobs}
        selectedResume={selectedResume || ''}
      />

      {showAddJobUrlModal && (
        <AddJobFromURL
          resumeid={selectedResume || ''}
          onClose={() => setShowAddJobUrlModal(false)}
          onSuccess={loadJobs}
        />
      )}

      {showAddJobEmailModal && (
        <AddJobFromEmail
          onClose={() => setShowAddJobEmailModal(false)}
        />
      )}

      <PreferencesModal
        isOpen={showPreferencesModal}
        onClose={() => setShowPreferencesModal(false)}
      />

      {/* Writing Sample opened from the header Feature Selector. The Resume
          Editor mounts its own instance (with the section chip refresh);
          this one is standalone — the editor re-checks the fingerprint on
          open, so no refresh callback is needed here. */}
      <WritingSampleModal
        isOpen={showWritingSampleModal}
        onClose={() => setShowWritingSampleModal(false)}
        onSuccess={() => {}}
      />

      <ResumeEditor
        isOpen={showResumeEditor}
        onClose={() => setShowResumeEditor(false)}
        onSuccess={(savedName) => {
          // Do NOT close here — closing is controlled by the editor's
          // onClose (Save and Close / Cancel / X). Closing here would defeat
          // the "Save" (keep-open) button.
          // Transition a newly created resume into edit-mode so further saves
          // update instead of creating duplicates, and so the editor unlocks
          // edit-only features + reloads to show generated tags.
          if (savedName) setEditingResumeName(savedName);
          // Refresh resume list in Header and reload job data (no full page reload)
          setResumeRefreshKey(prev => prev + 1);
          loadJobs(true);
        }}
        resumename={editingResumeName}
        focusTarget={editorFocusTarget}
        onDeleteResume={handleDeleteResume}
        onSwitchResume={(name) => {
          // Same state the header selector drives — keep the app's selected
          // resume and the editor's resume in sync, then let the editor's
          // load effect pull the new resume.
          handleSelectResume(name);
          setEditingResumeName(name);
        }}
        onNewResume={handleNewResume}
        onImpliedFactsRun={(resumename, runId) => setImpliedFactsRun({ resumename, runId })}
      />

      {/* Non-blocking "still working" pill while the post-close extraction runs.
          It never blocks anything — the user is back on the dashboard. */}
      {impliedFactsRun && !impliedFactsReview && (
        <div
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 px-3 py-2 rounded-full bg-white shadow-lg border border-gray-200 text-sm text-gray-700"
          role="status"
          data-testid="implied-facts-pill"
        >
          <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
          Snappy is checking your resume for implied facts…
        </div>
      )}

      {impliedFactsReview && (
        <ImpliedFactsReviewModal
          isOpen={true}
          resumename={impliedFactsReview.resumename}
          facts={impliedFactsReview.facts}
          // Persist ONLY. The modal saves on every per-item answer (confirm /
          // trash / edit), so closing here would slam it shut on the first
          // click — it owns when to close, via onDismiss.
          onSave={(facts) => saveImpliedFacts(impliedFactsReview.resumename, facts)}
          onDismiss={() => setImpliedFactsReview(null)}
        />
      )}

      {editingSubcomponent && (
        <SubcomponentEditor
          isOpen={true}
          onClose={() => setEditingSubcomponent(null)}
          onSave={handleSaveSubcomponent}
          jobid={editingSubcomponent.jobid}
          component={editingSubcomponent.component}
          initialContent={editingSubcomponent.content}
        />
      )}

      {editingJob && (
        <JobDescriptionEditor
          jobid={editingJob.jobid}
          initialContent={editingJob.jobdesc}
          initialTags={editingJob.jobtags}
          initialSuperTags={editingJob.super_tags}
          initialTitle={editingJob.jobtitle}
          initialCompany={editingJob.jobcompany}
          initialLocation={editingJob.joblocation}
          initialSalary={editingJob.jobsalary}
          initialReqid={editingJob.jobreqid}
          initialNotes={editingJob.jobnotes}
          initialTab={editingJob.initialTab}
          tagsGenerating={editingJob.tagsGenerating}
          onClose={() => setEditingJob(null)}
          onSave={loadJobs}
          onRegenerateTags={handleRegenerateTags}
        />
      )}

      <UpgradePromptModal
        isOpen={upgradeModal.open}
        onClose={() => setUpgradeModal(prev => ({ ...prev, open: false }))}
        limitType={upgradeModal.type}
        onNavigateToSettings={() => {
          setUpgradeModal(prev => ({ ...prev, open: false }));
          setShowPreferencesModal(true);
        }}
        onProceed={() => {
          setEditingResumeName(undefined);
          setShowResumeEditor(true);
        }}
      />

      <StripeCheckoutModal
        isOpen={showStripeCheckout}
        onClose={() => setShowStripeCheckout(false)}
        onSuccess={() => {
          setShowStripeCheckout(false);
          // Refresh preferences to reflect new subscription status
          api.getPreferences().then((prefs) => {
            setUserPreferences(prefs);
          }).catch((err) => {
            console.error('Failed to refresh preferences after checkout:', err);
          });
        }}
      />

      <UpgradeModal
        isOpen={freeGenUpgradeModal.open}
        onClose={() => setFreeGenUpgradeModal({ open: false, reason: 'general' })}
        onUpgrade={() => setShowStripeCheckout(true)}
        reason={freeGenUpgradeModal.reason}
        freeGenRemaining={userPreferences?.free_gen_remaining}
      />
    </div>
  );
}
